import type {
  BoardPosition,
  Coordinate,
  WarriorInstance,
  Direction,
  CardinalDirection,
} from '@/types';
import type { BoardState } from '@/types/game.types';
import type { AvailableActions } from '@/types/board.types';

const BOARD_SIZE = 5;

/**
 * Direction vectors for movement
 */
const DIRECTION_VECTORS: Record<Direction, Coordinate> = {
  n: { x: 0, y: -1 },
  ne: { x: 1, y: -1 },
  e: { x: 1, y: 0 },
  se: { x: 1, y: 1 },
  s: { x: 0, y: 1 },
  sw: { x: -1, y: 1 },
  w: { x: -1, y: 0 },
  nw: { x: -1, y: -1 },
};

/**
 * Cardinal directions for dashing
 */
const CARDINAL_DIRECTIONS: CardinalDirection[] = ['n', 'e', 's', 'w'];

/**
 * All 8 directions for movement
 */
const ALL_DIRECTIONS: Direction[] = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];

export class BoardManager {
  /**
   * Check if a position is within board bounds
   */
  static isValidPosition(pos: BoardPosition): boolean {
    return (
      pos.row >= 0 &&
      pos.row < BOARD_SIZE &&
      pos.col >= 0 &&
      pos.col < BOARD_SIZE
    );
  }

  /**
   * Check if a position is empty on the board
   */
  static isPositionEmpty(board: BoardState, pos: BoardPosition): boolean {
    if (!this.isValidPosition(pos)) return false;
    return board[pos.row][pos.col] === null;
  }

  /**
   * Get warrior instance ID at a position
   */
  static getWarriorAt(
    board: BoardState,
    pos: BoardPosition
  ): string | null {
    if (!this.isValidPosition(pos)) return null;
    return board[pos.row][pos.col];
  }

  /**
   * Calculate new position after moving in a direction
   */
  static getPositionInDirection(
    from: BoardPosition,
    direction: Direction,
    distance: number = 1
  ): BoardPosition {
    const vector = DIRECTION_VECTORS[direction];
    return {
      row: from.row + vector.y * distance,
      col: from.col + vector.x * distance,
    };
  }

  /**
   * Get all valid move positions for a warrior (1 tile, 8 directions)
   */
  static getValidMovePositions(
    board: BoardState,
    from: BoardPosition
  ): BoardPosition[] {
    const positions: BoardPosition[] = [];
    for (const dir of ALL_DIRECTIONS) {
      const newPos = this.getPositionInDirection(from, dir);
      if (this.isValidPosition(newPos) && this.isPositionEmpty(board, newPos)) {
        positions.push(newPos);
      }
    }
    return positions;
  }

  /**
   * Get all valid dash positions for a warrior (2 tiles, 4 cardinal directions)
   */
  static getValidDashPositions(
    board: BoardState,
    from: BoardPosition
  ): BoardPosition[] {
    const positions: BoardPosition[] = [];
    for (const dir of CARDINAL_DIRECTIONS) {
      const intermediate = this.getPositionInDirection(from, dir, 1);
      const destination = this.getPositionInDirection(from, dir, 2);

      // Both intermediate and destination must be valid and empty
      if (
        this.isValidPosition(intermediate) &&
        this.isPositionEmpty(board, intermediate) &&
        this.isValidPosition(destination) &&
        this.isPositionEmpty(board, destination)
      ) {
        positions.push(destination);
      }
    }
    return positions;
  }

  /**
   * Get attack target positions based on warrior's attack pattern
   */
  static getAttackTargetPositions(
    from: BoardPosition,
    attackPattern: readonly Coordinate[],
    isPlayer2: boolean
  ): BoardPosition[] {
    const positions: BoardPosition[] = [];
    for (const offset of attackPattern) {
      // Player 2's pattern is mirrored (y inverted)
      const adjustedY = isPlayer2 ? -offset.y : offset.y;
      const newPos: BoardPosition = {
        row: from.row + adjustedY,
        col: from.col + offset.x,
      };
      if (this.isValidPosition(newPos)) {
        positions.push(newPos);
      }
    }
    return positions;
  }

  /**
   * Get adjacent positions for healing (8 directions)
   */
  static getAdjacentPositions(from: BoardPosition): BoardPosition[] {
    const positions: BoardPosition[] = [];
    for (const dir of ALL_DIRECTIONS) {
      const newPos = this.getPositionInDirection(from, dir);
      if (this.isValidPosition(newPos)) {
        positions.push(newPos);
      }
    }
    return positions;
  }

  /**
   * Calculate all available actions for a warrior
   */
  static getAvailableActions(
    warrior: WarriorInstance,
    board: BoardState,
    allWarriors: WarriorInstance[]
  ): AvailableActions {
    if (!warrior.position) {
      return {
        canMove: false,
        validMovePositions: [],
        canDash: false,
        validDashPositions: [],
        canAttack: false,
        attackTargetPositions: [],
        canHeal: false,
        healTargetPositions: [],
      };
    }

    const validMovePositions = this.getValidMovePositions(board, warrior.position);

    // Dash available if dashFrequency > 0 and cooldown is met
    const canDash =
      warrior.definition.stats.dashFrequency > 0 &&
      warrior.dashCooldown >= warrior.definition.stats.dashFrequency;
    const validDashPositions = canDash
      ? this.getValidDashPositions(board, warrior.position)
      : [];

    // Attack available if frequency is 0 (always ready) or cooldown is met
    const attackFrequency = warrior.definition.stats.attackFrequency;
    const canAttack = attackFrequency === 0 || warrior.attackCooldown >= attackFrequency;
    const isPlayer2 = warrior.ownerId === 'player2';
    const attackTargetPositions = canAttack
      ? this.getAttackTargetPositions(
          warrior.position,
          warrior.definition.attackPattern,
          isPlayer2
        )
      : [];

    // Heal available if healingPower > 0 and cooldown is met (0 = always ready)
    const healingFrequency = warrior.definition.stats.healingFrequency;
    const canHeal =
      warrior.definition.stats.healingPower > 0 &&
      (healingFrequency === 0 || warrior.healCooldown >= healingFrequency);

    // Heal targets all team warriors (not just adjacent)
    const healTargetPositions: BoardPosition[] = canHeal
      ? allWarriors
          .filter(w => w.ownerId === warrior.ownerId && w.position !== null)
          .map(w => w.position!)
      : [];

    return {
      canMove: validMovePositions.length > 0,
      validMovePositions,
      canDash: canDash && validDashPositions.length > 0,
      validDashPositions,
      canAttack,
      attackTargetPositions,
      canHeal,
      healTargetPositions,
    };
  }

  /**
   * Create an empty board
   */
  static createEmptyBoard(): BoardState {
    return Array(BOARD_SIZE)
      .fill(null)
      .map(() => Array(BOARD_SIZE).fill(null));
  }

  /**
   * Clone a board state (immutable update)
   */
  static cloneBoard(board: BoardState): BoardState {
    return board.map(row => [...row]);
  }

  /**
   * Place a warrior on the board
   */
  static placeWarrior(
    board: BoardState,
    warriorInstanceId: string,
    position: BoardPosition
  ): BoardState {
    const newBoard = this.cloneBoard(board);
    newBoard[position.row][position.col] = warriorInstanceId;
    return newBoard;
  }

  /**
   * Remove a warrior from the board
   */
  static removeWarrior(board: BoardState, position: BoardPosition): BoardState {
    const newBoard = this.cloneBoard(board);
    newBoard[position.row][position.col] = null;
    return newBoard;
  }

  /**
   * Move a warrior on the board
   */
  static moveWarrior(
    board: BoardState,
    from: BoardPosition,
    to: BoardPosition
  ): BoardState {
    const warriorId = this.getWarriorAt(board, from);
    if (!warriorId) return board;

    let newBoard = this.removeWarrior(board, from);
    newBoard = this.placeWarrior(newBoard, warriorId, to);
    return newBoard;
  }
}
